import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAccount, useLogout } from '../../hooks/useAccount';

type LayoutProps = {
    children: React.ReactNode;
};

/**
 * ── Why the main column is not one width ───────────────────────────────────
 *
 * Prose wants ~65 characters. A six-column table of sold listings wants twice
 * that, and a hero set at 4.5rem wants room to break where the sentence breaks
 * rather than where the container does. One 48rem column served all three and
 * served none of them: the demo table clipped its condition column mid-word,
 * and the landing page read as a README because every element on it was the
 * width of a paragraph.
 *
 * So the width is a property of the route, resolved here rather than repeated
 * as a `max-w-` on every page root. Three widths, and a page belongs to exactly
 * one of them. Anything else and pages start disagreeing about the grid.
 */
const WIDTH = {
    /** Tables, card grids, the hero. */
    wide: 'max-w-6xl',
    /** Running text: docs, terms, contact. */
    prose: 'max-w-[46rem]',
    /** A single form with nothing beside it. */
    form: 'max-w-lg',
} as const;

export type PageWidth = keyof typeof WIDTH;

/**
 * Exported for the unit test: an unlisted route must fall back to `prose`,
 * because a new page is far more likely to be text than a data grid, and text
 * at 72rem is unreadable in a way that a table at 46rem is merely cramped.
 */
export function pageWidth(pathname: string): PageWidth {
    if (pathname === '/' || pathname === '/pricing' || pathname === '/app') return 'wide';
    if (pathname === '/signin' || pathname === '/auth/verify' || pathname === '/checkout/success') {
        return 'form';
    }
    return 'prose';
}

export function Layout({ children }: LayoutProps) {
    const account = useAccount();
    const logout = useLogout();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const signedIn = Boolean(account.data);
    // Chrome stays on the wide grid at every route, so the wordmark sits at the
    // same x-position whether you are reading terms or the landing page.
    const shell = 'mx-auto w-full max-w-6xl px-5 sm:px-8';

    return (
        <div className="flex min-h-screen flex-col bg-bg">
            <header className="sticky top-0 z-30 border-b border-border bg-bg/85 backdrop-blur-sm">
                <div className={`${shell} flex items-center justify-between gap-6 py-4`}>
                    <Link to={signedIn ? '/app' : '/'} className="text-xl leading-none font-bold tracking-[-0.03em]">
                        ActualSold
                    </Link>

                    <nav className="flex items-center gap-5 text-sm">
                        <Link to="/pricing" className="text-text-muted hover:text-text hover:underline">
                            Pricing
                        </Link>
                        <Link to="/docs" className="text-text-muted hover:text-text hover:underline">
                            Docs
                        </Link>
                        {signedIn ? (
                            <>
                                <Link to="/app" className="text-text-muted hover:text-text hover:underline">
                                    Console
                                </Link>
                                <span className="hidden text-text-muted sm:inline">{account.data?.user.email}</span>
                                <button
                                    onClick={() => logout.mutate(undefined, { onSuccess: () => navigate('/') })}
                                    className="text-text-muted hover:text-text hover:underline"
                                >
                                    Sign out
                                </button>
                            </>
                        ) : (
                            <Link to="/signin" className="text-text-muted hover:text-text hover:underline">
                                Sign in
                            </Link>
                        )}
                    </nav>
                </div>
            </header>

            <main className={`${shell} flex-1 py-12 sm:py-16`}>
                <div className={`w-full ${WIDTH[pageWidth(pathname)]}`}>{children}</div>
            </main>

            <footer className="mt-8 border-t border-border">
                <div className={`${shell} flex flex-wrap items-start justify-between gap-6 py-8 text-sm text-text-muted`}>
                    {/*
                      A date, not a claim. The question behind "is this
                      maintained" is answered better by a link to what actually
                      shipped last week than by any sentence we could write
                      about ourselves, and the date goes stale on its own if we
                      stop, which is the point.
                    */}
                    <div className="space-y-1.5">
                        <p>Independent research tool, not affiliated with eBay Inc.</p>
                        <p>Copyright 2026 ActualSold. All rights reserved.</p>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                        <Link to="/pricing" className="hover:text-text hover:underline">
                            Pricing
                        </Link>
                        <Link to="/docs" className="hover:text-text hover:underline">
                            Docs
                        </Link>
                        <Link to="/changelog" className="hover:text-text hover:underline">
                            Changelog
                        </Link>
                        <Link to="/terms" className="hover:text-text hover:underline">
                            Terms
                        </Link>
                        <Link to="/privacy" className="hover:text-text hover:underline">
                            Privacy
                        </Link>
                        <Link to="/refunds" className="hover:text-text hover:underline">
                            Refunds
                        </Link>
                        <Link to="/contact" className="hover:text-text hover:underline">
                            Contact
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
